'use client';

import { useState, useMemo, useEffect } from 'react';
import { ArrowUpRight, ChevronLeft, ChevronRight, Tag } from 'lucide-react';
import SectionShapes from '@shared/SectionShapes';
import { Carousel, CarouselContent, CarouselItem } from '@ui/carousel';
import Link from 'next/link';
import Image from 'next/image';

// Reveal props (see globals.css). onMount: animate as soon as it renders (cards
// swapped in by the category tabs) rather than when scrolled into view.
const fadeUp = (onMount = false) => ({
  'data-reveal': 'up',
  ...(onMount && { 'data-revealed': '' }),
  style: { '--reveal-distance': '28px', '--reveal-duration': '0.55s' },
});

function SliderDots({ count, current, onSelect }) {
  if (count <= 1) return null;
  return (
    <div className="flex items-center">
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          onClick={() => onSelect(i)}
          aria-label={`Slide ${i + 1}`}
          className="min-w-6 min-h-6 p-1.5 flex items-center justify-center cursor-pointer"
        >
          <span
            className={`block h-1.5 rounded-full transition-all duration-300 ${i === current ? 'w-5 bg-primary' : 'w-1.5 bg-border'
              }`}
          />
        </button>
      ))}
    </div>
  );
}

function SliderControls({ count, current, api }) {
  if (!api || count <= 1) return null;
  return (
    <div className="flex md:hidden items-center justify-center gap-4 mt-6">
      <button
        onClick={() => api.scrollPrev()}
        aria-label="Previous"
        className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors duration-200 cursor-pointer"
      >
        <ChevronLeft size={16} />
      </button>
      <SliderDots count={count} current={current} onSelect={(i) => api.scrollTo(i)} />
      <button
        onClick={() => api.scrollNext()}
        aria-label="Next"
        className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors duration-200 cursor-pointer"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

function BlogCard({ post }) {
  return (
    <article
      {...fadeUp(true)}
      className="group flex flex-col h-full bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-xl hover:border-primary/30 transition-all duration-300"
    >
      <div className="relative h-40 w-full overflow-hidden bg-muted/20">
        {post.image ? (
          <Image
            src={post.image}
            alt={post.title}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-500"
            sizes="(max-width: 768px) 100vw, 33vw"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl font-black text-muted-foreground/15">VA</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-white/90 text-primary backdrop-blur-sm">
          <Tag size={9} /> {post.tag}
        </span>
      </div>

      <div className="flex flex-col gap-3 p-5 flex-1">
        <h3 className="text-base font-bold text-ink leading-snug">
          <Link
            href={`/blog/${post.slug}`}
            className="hover:text-primary transition-colors duration-300"
          >
            {post.title}
          </Link>
        </h3>
        <p className="text-mute text-sm flex-1 line-clamp-2">
          {post.excerpt}
        </p>
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500">{post.date}</span>
          <Link
            href={`/blog/${post.slug}`}
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:gap-2 transition-all duration-300"
          >
            {/* Hidden title makes the anchor text descriptive for crawlers and screen readers. */}
            Read More<span className="sr-only"> about {post.title}</span> <ArrowUpRight size={12} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
}

const BENTO_CLASSES = [
  'md:col-start-1 md:row-start-1',
  'md:col-start-2 md:row-start-1',
  'md:col-start-3 md:row-start-1 md:row-span-2',
  'md:col-start-1 md:col-span-1 md:row-start-2',
  'md:col-start-2 md:col-span-1 md:row-start-2',
];

function BentoStudyCard({ study, bentoClass }) {

  return (
    <div
      {...fadeUp(true)}
      className={`group relative overflow-hidden rounded-2xl cursor-pointer w-full h-full min-h-[200px] bg-muted/20 ${bentoClass}`}
    >
      {study.image ? (
        <Image
          src={study.image}
          alt={study.title}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-500"
          sizes="(max-width: 768px) 100vw, 50vw"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-4xl font-black text-muted-foreground/15">VA</div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10 group-hover:from-black/90 transition-all duration-300" />

      <div className="absolute inset-0 flex flex-col justify-between p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap gap-1.5">
            {study.industry && (
              <span className="px-2.5 py-1 rounded-md bg-black/40 backdrop-blur-md text-white text-[10px] font-bold tracking-wider border border-white/20 shadow-sm">
                {study.industry}
              </span>
            )}
          </div>
          <Link
            title="Read More"
            href={`/case-studies/${study.slug}`}
            className="shrink-0 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-all duration-300"
          >
            <span className="sr-only">Read case study: {study.title}</span>
            <ArrowUpRight size={14} className="text-white" aria-hidden="true" />
          </Link>
        </div>

        <div>
          {study.result && (
            <span className="mb-2 inline-block px-3 py-1 rounded-full bg-primary text-white text-xs font-semibold shadow-md">
              {study.result}
            </span>
          )}
          <Link href={`/case-studies/${study.slug}`} className="line-clamp-2">
            <h3 className="text-white font-bold text-base leading-snug [text-shadow:0_1px_8px_rgba(0,0,0,0.8)] hover:text-primary transition-colors duration-200">
              {study.title}
            </h3>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function BlogSectionClient({
  posts = [],
  caseStudies = [],
  blogHref = '/blog',
  caseStudiesHref = '/case-studies',
}) {
  const [activeBlogCat] = useState('all');
  const [activeCaseCat] = useState('all');

  const [blogApi, setBlogApi] = useState(null);
  const [blogCurrent, setBlogCurrent] = useState(0);
  const [caseApi, setCaseApi] = useState(null);
  const [caseCurrent, setCaseCurrent] = useState(0);

  useEffect(() => {
    if (!blogApi) return;
    const onSelect = () => setBlogCurrent(blogApi.selectedScrollSnap());
    onSelect();
    blogApi.on('select', onSelect);
    blogApi.on('reInit', onSelect);
    return () => blogApi.off('select', onSelect);
  }, [blogApi]);

  useEffect(() => {
    if (!caseApi) return;
    const onSelect = () => setCaseCurrent(caseApi.selectedScrollSnap());
    onSelect();
    caseApi.on('select', onSelect);
    caseApi.on('reInit', onSelect);
    return () => caseApi.off('select', onSelect);
  }, [caseApi]);

  const visiblePosts = useMemo(() => {
    const filtered = activeBlogCat === 'all'
      ? posts
      : posts.filter((p) => p.categorySlug === activeBlogCat);
    return filtered.slice(0, 3);
  }, [posts, activeBlogCat]);

  const visibleStudies = useMemo(() => {
    const filtered = activeCaseCat === 'all'
      ? caseStudies
      : caseStudies.filter((s) => s.categorySlug === activeCaseCat);
    return filtered.slice(0, 5);
  }, [caseStudies, activeCaseCat]);

  return (
    <section className="relative py-12 md:py-20 overflow-hidden bg-[#f7f8fc]">
      <div className="relative z-10 max-w-[1440px] mx-auto px-6">
        <SectionShapes variant="dark" />

        {/* ── Article section ── */}
        <div className='flex flex-col sm:flex-row justify-between sm:items-center gap-6 sm:gap-10'>
          <div
            className="mb-0 sm:mb-8"
            {...fadeUp()}
          >
            <span className="flex flex-wrap items-center gap-2 my-4 text-primary undefined">
              <svg width="24" height="6" viewBox="0 0 24 6" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M23.5 5C23.7761 5 24 5.22386 24 5.5C24 5.77614 23.7761 6 23.5 6L8.5 6C8.22386 6 8 5.77614 8 5.5C8 5.22386 8.22386 5 8.5 5L23.5 5Z"></path>
                <path d="M23.5 2.05443e-06C23.7761 2.07858e-06 24 0.22386 24 0.500002C24 0.776144 23.7761 1 23.5 1L0.5 1C0.223858 1 -6.78525e-08 0.776142 -4.37114e-08 0.5C-1.95703e-08 0.223858 0.223858 1.95703e-08 0.5 4.37114e-08L23.5 2.05443e-06Z"></path>
              </svg>
              <span className="block font-sans font-medium text-xs tracking-widest uppercase text-secondary group-[.text-white]:text-accent">
                What Is Our Work
              </span>
            </span>
            <h2 className="group flex-wrap flex-row gap-0 md:gap-2 font-serif font-black text-4xl leading-12 mb-6 text-ink">
              Read Our <em className="text-grad-red not-italic">Articles</em>
            </h2>
            <p className="text-mute max-w-3xl mx-auto -mt-4">
              See our blog for the most recent information on industry news, tech developments, and professional advice.
            </p>
          </div>
          <div
            className="mb-10 shrink-0"
            {...fadeUp()}
          >
            <Link
              href={blogHref}
              className="group inline-flex h-auto items-center gap-3 px-6 py-3 rounded-lg border border-primary/50 text-primary font-semibold text-sm hover:bg-primary hover:text-white hover:border-primary hover:shadow-[0_0_30px_rgba(224,39,74,0.25)] transition-all duration-300"
            >
              View All Articles
              <ArrowUpRight size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </div>
        </div>

        <div className="mb-20">
          {visiblePosts.length > 0 ? (
            <Carousel
              setApi={setBlogApi}
              opts={{ align: 'start', loop: false }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {visiblePosts.map((post) => (
                  <CarouselItem key={post.id} className="pl-4 basis-[85%] sm:basis-[65%] md:basis-1/3">
                    <BlogCard post={post} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          ) : (
            <p className="text-center text-muted-foreground py-10">
              No posts found in this category.
            </p>
          )}
          <SliderControls count={visiblePosts.length} current={blogCurrent} api={blogApi} />
        </div>

        {/* ── Case Studies section ── */}
        <div className='flex flex-col sm:flex-row justify-between sm:items-center gap-6 sm:gap-10'>
          <div
            className="mb-0 sm:mb-8"
            {...fadeUp()}
          >
            <div className='heading-group'>
              <span className="flex flex-wrap items-center gap-2 my-4 text-primary undefined">
                <svg width="24" height="6" viewBox="0 0 24 6" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23.5 5C23.7761 5 24 5.22386 24 5.5C24 5.77614 23.7761 6 23.5 6L8.5 6C8.22386 6 8 5.77614 8 5.5C8 5.22386 8.22386 5 8.5 5L23.5 5Z"></path>
                  <path d="M23.5 2.05443e-06C23.7761 2.07858e-06 24 0.22386 24 0.500002C24 0.776144 23.7761 1 23.5 1L0.5 1C0.223858 1 -6.78525e-08 0.776142 -4.37114e-08 0.5C-1.95703e-08 0.223858 0.223858 1.95703e-08 0.5 4.37114e-08L23.5 2.05443e-06Z"></path>
                </svg>
                <span className="block font-sans font-medium text-xs tracking-widest uppercase text-secondary group-[.text-white]:text-accent">
                  Explore the Expertise
                </span>
              </span>
              <h2 className="group flex flex-wrap flex-row gap-0 md:gap-2 font-serif font-black text-4xl leading-12 mb-6 text-ink">
                Explore Our <em className="text-grad-red not-italic">Case Studies</em>
              </h2>
            </div>
            <p className="text-mute max-w-3xl mx-auto -mt-4">
              This is where our clients vision met our expertise. Explore some of the masterpieces we created for our clients
            </p>
          </div>
          <div
            className="mb-10 shrink-0"
            {...fadeUp()}
          >
            <Link
              href={caseStudiesHref}
              className="group inline-flex h-auto items-center gap-3 px-6 py-3 rounded-lg border border-primary/50 text-primary font-semibold text-sm hover:bg-primary hover:text-white hover:border-primary hover:shadow-[0_0_30px_rgba(224,39,74,0.25)] transition-all duration-300"
            >
              View All Case Studies
              <ArrowUpRight size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </div>
        </div>

        {/* Desktop: bento grid */}
        <div
          className="hidden md:grid grid-cols-3 gap-3 min-h-[220px]"
          style={{ gridAutoRows: '220px' }}
        >
          {visibleStudies.map((study, i) => (
            <BentoStudyCard
              key={study.id}
              study={study}
              bentoClass={BENTO_CLASSES[i] ?? ''}
            />
          ))}
          {visibleStudies.length === 0 && (
            <p className="col-span-3 text-center text-muted-foreground py-10">
              No case studies found in this category.
            </p>
          )}
        </div>

        {/* Mobile: carousel */}
        <div className="md:hidden">
          {visibleStudies.length > 0 ? (
            <Carousel
              setApi={setCaseApi}
              opts={{ align: 'start', loop: false }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {visibleStudies.map((study) => (
                  <CarouselItem key={study.id} className="pl-4 basis-[85%] sm:basis-[65%] h-[240px]">
                    <BentoStudyCard study={study} bentoClass="" />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          ) : (
            <p className="text-center text-muted-foreground py-10">
              No case studies found in this category.
            </p>
          )}
          <SliderControls count={visibleStudies.length} current={caseCurrent} api={caseApi} />
        </div>
      </div>
    </section>
  );
}
